import React, { useState } from 'react';
import { Button } from '@ui';
import Loader, { LoaderSizes } from '@components/v2/Loader';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import Banner, { BannerVariant } from '@components/Banner';
import { translate } from '@utils/i18n';
import ConnectionWizard from './components/ConnectionWizard';
import SettingsForm from './components/SettingsForm';
import WebhookStatus from './components/WebhookStatus';
import { useQueueStatusQuery } from './hooks/useQueueStatusQuery';
import { useUpdateSettingsMutation, useTriggerSyncMutation, useDisconnectMutation } from './hooks/useServiceTradeMutations';
import './ServiceTradeSettingsStyles.scss';
const namespace = 'features.settings.service_trade';
export default function ServiceTradeSettings({ initialData }) {
    var _a;
    const [connection, setConnection] = useState(initialData);
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [isReloading, setIsReloading] = useState(false);
    const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
    // Queries
    const { data: queueStatus } = useQueueStatusQuery();
    // Mutations
    const updateSettingsMutation = useUpdateSettingsMutation();
    const triggerSyncMutation = useTriggerSyncMutation();
    const disconnectMutation = useDisconnectMutation();
    const handleConnectionComplete = () => {
        setIsReloading(true);
        setIsWizardOpen(false);
        // Reload the page to get updated connection data
        window.location.reload();
    };
    const handleSaveSettings = () => {
        // Store previous settings for rollback on error
        const previousSettings = Object.assign({}, connection.settings);
        updateSettingsMutation.mutate(connection.settings, {
            onError: () => {
                // Roll back to previous settings on error
                setConnection(Object.assign(Object.assign({}, connection), { settings: previousSettings }));
            }
        });
    };
    const handleTriggerSync = () => {
        triggerSyncMutation.mutate(connection.selectedOfficeIds);
    };
    const handleDisconnect = () => {
        setShowDisconnectConfirm(true);
    };
    const confirmDisconnect = () => {
        disconnectMutation.mutate(undefined, {
            onSettled: () => {
                setShowDisconnectConfirm(false);
            }
        });
    };
    // Show loading state during reload
    if (isReloading) {
        return (React.createElement("div", { className: "workspace__major--form", id: "service-trade-settings" },
            React.createElement("header", { className: "workspace__header--alt--sticky" },
                React.createElement("div", { className: "header__main" },
                    React.createElement("h1", { className: "workspace__title" }, translate('title', { namespace })))),
            React.createElement("section", { className: "workspace__section" },
                React.createElement("div", { className: "empty-state" },
                    React.createElement(Loader, { size: LoaderSizes.large }),
                    React.createElement("p", null, translate('connection.loading', { namespace }))))));
    }
    if (isWizardOpen) {
        return (React.createElement("div", { className: "workspace__major--form", id: "service-trade-settings" },
            React.createElement("header", { className: "workspace__header--alt--sticky" },
                React.createElement("div", { className: "header__main" },
                    React.createElement("h1", { className: "workspace__title" }, translate('title', { namespace })))),
            React.createElement("section", { className: "workspace__section" },
                React.createElement(ConnectionWizard, { onConnectionComplete: handleConnectionComplete, onCancel: () => setIsWizardOpen(false) }))));
    }
    if (!connection.hasIntegration) {
        return (React.createElement("div", { className: "workspace__major--form", id: "service-trade-settings" },
            React.createElement("header", { className: "workspace__header--alt--sticky" },
                React.createElement("div", { className: "header__main" },
                    React.createElement("h1", { className: "workspace__title" }, translate('title', { namespace })))),
            React.createElement("section", { className: "workspace__section--intro" },
                React.createElement("p", null, translate('description', { namespace }))),
            React.createElement("section", { className: "workspace__section" },
                React.createElement("div", { className: "empty-state" },
                    React.createElement("i", { className: "fa-light fa-plug empty-state__icon" }),
                    React.createElement("h2", null, translate('not_connected.title', { namespace })),
                    React.createElement("p", null, translate('not_connected.description', { namespace })),
                    React.createElement(Button, { variant: "primary", onClick: () => setIsWizardOpen(true) },
                        React.createElement("i", { className: "fa-light fa-plus" }),
                        translate('connect_button', { namespace }))))));
    }
    return (React.createElement("div", { className: "workspace__major--form", id: "service-trade-settings" },
        React.createElement("header", { className: "workspace__header--alt--sticky" },
            React.createElement("div", { className: "header__main" },
                React.createElement("h1", { className: "workspace__title" }, translate('title', { namespace }))),
            React.createElement("div", { className: "qmb-actions" },
                React.createElement(Button, { variant: "primary", onClick: handleSaveSettings, disabled: updateSettingsMutation.isLoading },
                    updateSettingsMutation.isLoading && React.createElement(Loader, { size: LoaderSizes.small }),
                    translate('save', { namespace: 'generic' })))),
        React.createElement("section", { className: "workspace__section--intro" },
            React.createElement("p", null, translate('description', { namespace }))),
        connection.needsReauth && (React.createElement(Banner, { variant: BannerVariant.Warning, text: translate('connection.needs_reauth_banner', { namespace }) })),
        React.createElement("section", { className: "workspace__section" },
            React.createElement("header", { className: "section__title" },
                React.createElement("h2", { className: "section__label" }, translate('connection.title', { namespace })),
                React.createElement("hr", { className: "section__split" })),
            React.createElement("div", { className: "connection-card" },
                React.createElement("div", { className: "connection-card__header" }, connection.needsReauth ? (React.createElement("div", { className: "connection-status connection-status--warning" },
                    React.createElement("i", { className: "fa-light fa-exclamation-triangle" }),
                    React.createElement("span", null, translate('connection.credentials_expired', { namespace })))) : (React.createElement("div", { className: "connection-status connection-status--connected" },
                    React.createElement("i", { className: "fa-light fa-check-circle" }),
                    React.createElement("span", null, translate('connection.connected', { namespace }))))),
                React.createElement("div", { className: "connection-card__body" },
                    React.createElement("div", { className: "connection-detail" },
                        React.createElement("label", null, translate('connection.username', { namespace })),
                        React.createElement("strong", null, connection.username)),
                    React.createElement("div", { className: "connection-detail" },
                        React.createElement("label", null, translate('connection.vendor_id', { namespace })),
                        React.createElement("strong", null, connection.vendorId)),
                    React.createElement("div", { className: "connection-detail" },
                        React.createElement("label", null, translate('connection.selected_offices', { namespace })),
                        React.createElement("strong", null, connection.selectedOfficeIds.length > 0
                            ? ((_a = connection.offices) !== null && _a !== void 0 ? _a : [])
                                .filter(o => connection.selectedOfficeIds.includes(o.id))
                                .map(o => o.name)
                                .join(', ')
                            : translate('connection.all_offices', { namespace }))),
                    React.createElement("div", { className: "connection-detail" },
                        React.createElement("label", null, translate('connection.linked_buildings', { namespace })),
                        React.createElement("strong", null, connection.linkedBuildingsCount)),
                    connection.queuePostfix && (React.createElement("div", { className: "connection-detail" },
                        React.createElement("label", null, translate('connection.queue_postfix', { namespace })),
                        React.createElement("strong", null, connection.queuePostfix))),
                    React.createElement("div", { className: "connection-detail" },
                        React.createElement("label", null, translate('connection.queue_depth', { namespace })),
                        React.createElement("strong", null, queueStatus ? (React.createElement(React.Fragment, null,
                            queueStatus.queue_depth,
                            queueStatus.status === 'backed_up' && (React.createElement("span", { style: { color: 'var(--color-status-02)', marginLeft: '0.5rem' } },
                                React.createElement("i", { className: "fa-light fa-exclamation-triangle" }))))) : (React.createElement("i", { className: "fa-light fa-circle-notch fa-spin" }))))),
                React.createElement("div", { className: "connection-card__footer" },
                    React.createElement(Button, { variant: "secondary", onClick: handleTriggerSync, disabled: triggerSyncMutation.isLoading },
                        triggerSyncMutation.isLoading && React.createElement(Loader, { size: LoaderSizes.small }),
                        React.createElement("i", { className: "fa-light fa-sync" }),
                        translate('connection.trigger_sync', { namespace })),
                    React.createElement(Button, { variant: "secondary", onClick: () => setIsWizardOpen(true) },
                        React.createElement("i", { className: "fa-light fa-arrows-rotate" }),
                        translate('connection.reconnect', { namespace })),
                    React.createElement(Button, { variant: "secondary", onClick: handleDisconnect, disabled: disconnectMutation.isLoading },
                        disconnectMutation.isLoading && React.createElement(Loader, { size: LoaderSizes.small }),
                        React.createElement("i", { className: "fa-light fa-plug-circle-xmark" }),
                        translate('connection.disconnect', { namespace }))))),
        React.createElement(WebhookStatus, { hasIntegration: connection.hasIntegration }),
        React.createElement("section", { className: "workspace__section" },
            React.createElement("header", { className: "section__title" },
                React.createElement("h2", { className: "section__label" }, translate('settings.title', { namespace })),
                React.createElement("hr", { className: "section__split" })),
            React.createElement(SettingsForm, { settings: connection.settings, hasCalifornia: connection.hasCalifornia, onChange: (settings) => setConnection(Object.assign(Object.assign({}, connection), { settings })) })),
        showDisconnectConfirm && (React.createElement(ConfirmDialog, { title: translate('connection.disconnect', { namespace }), description: translate('connection.confirm_disconnect', { namespace }), onConfirm: confirmDisconnect, onCancel: () => setShowDisconnectConfirm(false), loading: disconnectMutation.isLoading, yesLabel: translate('connection.disconnect', { namespace }), noLabel: translate('cancel', { namespace: 'generic' }), confirmButtonVariant: "danger" }))));
}
